import { formaPagamentoEhDesconto, reservaTemPagamentoCortesia, montarFormaPagamentoOptionFromApi, montarTipoPagamentoFromForma } from '../../utility/formaPagamentoTipo'
import { percentualTaxaClienteDoPagamento, montarTaxaClienteOptionFromPagamento } from '../../utility/taxaCliente'
import { prepararPagamentosAdiantamento } from '../../views/AcertoFinanceiro/validacaoAdiantamentoReembolso'
import { aReceberNoAcerto } from '../../views/AcertoFinanceiro/validacaoRecebimentoReserva'

/**
 * Pagamento da despesa de taxa maquininha com a forma da mesma operadora:
 * não aplica taxa de cartão nem acumula nova taxa (evita loop).
 */
function pagamentoEhPagamentoDaPropriaTaxaMaquininha(pagamento, fornecedorIdLinha) {
  const linhaId = Number(fornecedorIdLinha || 0);
  const operadoraId = Number(pagamento?.formaPagamento?.pag_fornecedor_id || 0);
  return linhaId > 0 && operadoraId > 0 && linhaId === operadoraId;
}

function valorPagamentoComTaxa(pagamento, fornecedorIdLinha = null) {
  const raw = pagamento?.valor ?? pagamento?.total;
  const valor = +String(raw ?? 0).replace(",", ".");
  if (pagamentoEhPagamentoDaPropriaTaxaMaquininha(pagamento, fornecedorIdLinha)) {
    return valor;
  }
  if (pagamento?.formaPagamento?.pag_credito !== "SIM") return valor;
  const taxaPercentual = getTaxaMaquininhaPercentual(pagamento);
  return valor + valor * (taxaPercentual / 100);
}

function toNumber(value) {
  const parsed = Number(String(value ?? 0).replace(",", "."));
  return Number.isFinite(parsed) ? parsed : 0;
}

function round2(value) {
  return Math.round((Number(value) + Number.EPSILON) * 100) / 100;
}

function recalcularTotaisPagamentoFornecedor(fornecedorCopy) {
  const fornecedorIdLinha = Number(fornecedorCopy?.fornecedor?.value || 0);
  let totalPago = 0;
  for (const pagamento of fornecedorCopy?.pagamentos || []) {
    if (pagamento?.formaPagamento?.value !== 1) {
      totalPago += valorPagamentoComTaxa(pagamento, fornecedorIdLinha);
    }
  }
  const total = Number(fornecedorCopy?.total || 0);
  fornecedorCopy.totalPago = totalPago;
  fornecedorCopy.faltaPagar = totalPago <= total ? total - totalPago : 0;
  return fornecedorCopy;
}

function normalizarComprovantesPagamento(comprovantes = []) {
  if (!Array.isArray(comprovantes)) return [];

  return comprovantes
    .map((comprovante) => {
      const arquivo = comprovante?.arquivo;
      if (!arquivo) return null;
      const arquivoString = String(arquivo);
      const urlArquivo = /^https?:\/\//i.test(arquivoString)
        ? arquivoString
        : `${process.env.REACT_APP_BACKEND_WEB}storage/${arquivoString}`;

      const nomeArquivo = arquivoString.split("/").pop() || "Comprovante";
      const extensao = nomeArquivo.includes(".")
        ? nomeArquivo.split(".").pop().toLowerCase()
        : "";
      const tipo =
        extensao === "pdf" ? "application/pdf" : `image/${extensao || "jpeg"}`;

      return {
        id: comprovante?.id ?? null,
        isExisting: true,
        name: nomeArquivo,
        url: urlArquivo,
        type: tipo,
        conferido: Boolean(comprovante?.conferido),
        dataConferencia: comprovante?.data_conferencia ?? null,
        // Devolvida ao salvar para o comprovante recriado manter a data de inserção original.
        dataAdicionado: comprovante?.data_adicionado ?? null,
      };
    })
    .filter(Boolean);
}

function normalizarComprovantesArquivos(comprovantesArquivos = []) {
  if (!Array.isArray(comprovantesArquivos)) return [];

  return comprovantesArquivos
    .map((comprovante) => {
      if (comprovante instanceof File) return comprovante;
      if (!comprovante || typeof comprovante !== "object") return comprovante;

      const urlOriginal = comprovante?.url ? String(comprovante.url) : "";
      if (!urlOriginal) return comprovante;

      const caminhoNormalizado = urlOriginal
        .replace(/^\/+/, "")
        .replace(/^storage\/+/i, "");
      const urlArquivo = /^https?:\/\//i.test(urlOriginal)
        ? urlOriginal
        : `${process.env.REACT_APP_BACKEND_WEB}storage/${caminhoNormalizado}`;

      return {
        ...comprovante,
        isExisting: comprovante?.isExisting ?? true,
        url: urlArquivo,
      };
    })
    .filter(Boolean);
}

function normalizarPagamentoFornecedor(pagamento = {}) {
  const forma = pagamento?.formaPagamento || pagamento?.pagamento_forma || pagamento?.forma;
  const banco = pagamento?.banco;
  const parcela = pagamento?.parcela;
  const fonte = pagamento?.fontePagamento || pagamento?.fonte;

  return {
    ...pagamento,
    banco:
      banco && typeof banco === "object" && Object.prototype.hasOwnProperty.call(banco, "value")
        ? banco
        : banco?.ban_nome
        ? { label: banco.ban_nome, value: banco.ban_id ?? banco.id ?? 0 }
        : { label: "Selecione um banco!", value: 0 },
    parcela:
      parcela && typeof parcela === "object" && Object.prototype.hasOwnProperty.call(parcela, "value")
        ? parcela
        : parcela?.par_id
        ? { label: parcela.par_nome, value: parcela.par_id }
        : { label: "1X", value: 1 },
    fontePagamento:
      fonte && typeof fonte === "object" && Object.prototype.hasOwnProperty.call(fonte, "value")
        ? fonte
        : fonte?.id
        ? { label: fonte.nome, value: fonte.id }
        : { label: "Selecione a fonte de pagamento!", value: 0 },
    formaPagamento:
      forma && typeof forma === "object" && Object.prototype.hasOwnProperty.call(forma, "value")
        ? forma
        : {
            label: forma?.pag_nome || "Selecione a forma de pagamento!",
            value: forma?.pag_id ?? pagamento?.pagamento_id ?? 0,
            pag_credito: forma?.pag_credito ?? null,
            pag_cash: forma?.pag_cash ?? null,
            pag_debito: forma?.pag_debito ?? null,
            pag_idtipo: forma?.pag_idtipo ?? null,
            tipo_nome: forma?.tipo_nome ?? forma?.tipo?.nome ?? null,
            pag_taxa: forma?.pag_taxa ?? null,
            pag_taxa_parcelado: forma?.pag_taxa_parcelado ?? null,
            pag_fornecedor_id: forma?.pag_fornecedor_id ?? null,
            pag_fornecedor_nome: forma?.fornecedor?.pessoa?.pes_nome ?? null,
          },
    comprovantesArquivos: Array.isArray(pagamento?.comprovantesArquivos)
      ? normalizarComprovantesArquivos(pagamento.comprovantesArquivos)
      : normalizarComprovantesPagamento(pagamento?.comprovantes),
  };
}

/**
 * Monta o estado da lista de adiantamentos/reembolsos a partir da API (snake ou camelCase).
 * Garante adiantamento_reembolso_tipo_id para linhas salvas com catálogo (tipo "cat_{id}" no banco).
 */
function normalizarTipoTransacao(raw) {
  if (raw == null) return null;
  const u = String(raw).toUpperCase().trim();
  return u === "CREDITO" || u === "DEBITO" ? u : null;
}

function defaultFormaPagamentoAdiantamento() {
  return {
    label: "Selecione a forma de pagamento!",
    value: "",
    pag_credito: null,
    pag_cash: null,
    pag_debito: null,
    pag_idtipo: null,
    tipo_nome: null,
    pag_taxa: null,
    pag_taxa_parcelado: null,
    pag_fornecedor_id: null,
    pag_fornecedor_nome: null,
  };
}

function defaultPagamentoAdiantamentoRow(valor = "0") {
  return {
    valor: valor != null ? String(valor) : "0",
    formaPagamento: defaultFormaPagamentoAdiantamento(),
    banco: { label: "Selecione um banco!", value: 0 },
    parcela: { label: "1X", value: 1 },
    fontePagamento: { label: "Selecione a fonte de pagamento!", value: 0 },
  };
}

const ID_TIPO_DEVOLUCAO_GUIA = 3;

// Identifica o item "Devolução do guia" (id 3 do catálogo) — único onde FATURADO/GRATIS
// não contam como pago (mesma regra aplicada na fatura do guia).
function itemEhDevolucaoGuia(item) {
  const tipoId = Number(item?.adiantamento_reembolso_tipo_id ?? 0);
  if (tipoId === ID_TIPO_DEVOLUCAO_GUIA) return true;
  const m = String(item?.tipo ?? "").match(/^cat_(\d+)$/i);
  return m ? Number(m[1]) === ID_TIPO_DEVOLUCAO_GUIA : false;
}

// Tipos de pagamento que NÃO contam como pago na devolução (viram "falta pagar").
const TIPOS_PAGAMENTO_NAO_ABATEM = ["FATURADO", "GRATIS"];

function pagamentoAdiantamentoAbate(pagamento) {
  const forma = pagamento?.formaPagamento || {};
  const tipoNome = String(forma?.tipo_nome ?? "").trim().toUpperCase();
  if (TIPOS_PAGAMENTO_NAO_ABATEM.includes(tipoNome)) return false;
  if (Number(forma?.pag_idtipo) === 6) return false; // 6 = FATURADO
  return true;
}

function calcularTotaisPagamentoAdiantamento(valorTotal, pagamentos = [], ehDevolucaoGuia = false) {
  const total = toNumber(valorTotal);
  let totalPago = 0;
  for (const pagamento of pagamentos || []) {
    const formaValue = pagamento?.formaPagamento?.value;
    if (!formaValue || Number(formaValue) <= 0) continue;
    // Na devolução do guia, FATURADO/GRATIS não abatem (viram falta pagar).
    if (ehDevolucaoGuia && !pagamentoAdiantamentoAbate(pagamento)) continue;
    totalPago += toNumber(pagamento?.valor ?? 0);
  }
  return {
    totalPago: round2(totalPago),
    faltaPagar: round2(Math.max(0, total - totalPago)),
  };
}

function defaultAdiantamentoItem() {
  return {
    adiantamento_reembolso_tipo_id: null,
    tipo: "",
    tipo_transacao: null,
    valor: "0",
    parceiro_id: null,
    pagamentos: [defaultPagamentoAdiantamentoRow("0")],
    totalPago: 0,
    faltaPagar: 0,
  };
}

function normalizePagamentosAdiantamentoFromApi(item) {
  const raw =
    item?.pagamentos ||
    item?.acerto_adiantamento_reembolso_pagamentos ||
    item?.acertoAdiantamentoReembolsoPagamentos ||
    [];

  if (Array.isArray(raw) && raw.length > 0) {
    return raw.map((pagamento) => normalizarPagamentoFornecedor(pagamento));
  }

  if (item?.pagamento_id || item?.pagamentoForma || item?.pagamento_forma) {
    return [
      normalizarPagamentoFornecedor({
        valor: item.valor,
        formaPagamento: normalizeFormaPagamentoAdiantamentoFromApi(item),
        banco: item.banco,
        parcela: item.parcela,
        fontePagamento: item.fontePagamento || item.fonte,
        banco_id: item.banco_id,
        parcela_id: item.parcela_id,
        fonte_pagamento_id: item.fonte_pagamento_id,
      }),
    ];
  }

  return [defaultPagamentoAdiantamentoRow(item?.valor ?? "0")];
}

function normalizeFormaPagamentoAdiantamentoFromApi(item) {
  const forma =
    item?.formaPagamento ||
    item?.pagamento_forma ||
    item?.pagamentoForma ||
    item?.forma;

  if (forma && typeof forma === "object" && Object.prototype.hasOwnProperty.call(forma, "value")) {
    return forma;
  }

  const pagId = item?.pagamento_id ?? forma?.pag_id ?? null;
  if (pagId == null || pagId === "" || Number(pagId) <= 0) {
    return defaultFormaPagamentoAdiantamento();
  }

  return {
    label: forma?.pag_nome ?? forma?.label ?? `Forma #${pagId}`,
    value: pagId,
    pag_credito: forma?.pag_credito ?? null,
    pag_cash: forma?.pag_cash ?? null,
    pag_debito: forma?.pag_debito ?? null,
    pag_idtipo: forma?.pag_idtipo ?? null,
    tipo_nome: forma?.tipo_nome ?? forma?.tipo?.nome ?? null,
    pag_taxa: forma?.pag_taxa ?? null,
    pag_taxa_parcelado: forma?.pag_taxa_parcelado ?? null,
    pag_fornecedor_id: forma?.pag_fornecedor_id ?? null,
    pag_fornecedor_nome: forma?.fornecedor?.pessoa?.pes_nome ?? null,
  };
}

function normalizeAdiantamentoItemFromApi(item) {
  if (!item || typeof item !== "object") {
    return defaultAdiantamentoItem();
  }

  const tipoCatalogo = item.tipo_catalogo ?? item.tipoCatalogo;
  let catalogId =
    item.adiantamento_reembolso_tipo_id ??
    item.adiantamentoReembolsoTipoId ??
    (tipoCatalogo != null ? tipoCatalogo.id : null);

  if (catalogId !== null && catalogId !== undefined && catalogId !== "") {
    catalogId = Number(catalogId);
    if (!Number.isFinite(catalogId)) catalogId = null;
  } else {
    catalogId = null;
  }

  const tipoRaw = item.tipo != null && item.tipo !== undefined ? String(item.tipo) : "";
  if (!catalogId && tipoRaw) {
    const m = tipoRaw.match(/^cat_(\d+)$/i);
    if (m) catalogId = Number(m[1]);
  }

  let parceiroId = item.parceiro_id ?? item.parceiroId ?? null;
  if (parceiroId !== null && parceiroId !== undefined && parceiroId !== "") {
    parceiroId = Number(parceiroId);
    if (!Number.isFinite(parceiroId)) parceiroId = null;
  } else {
    parceiroId = null;
  }

  let valor = item.valor;
  if (valor != null && valor !== "") {
    valor = String(valor).replace(".", ",");
  } else {
    valor = "0";
  }

  const tipoTransacao =
    normalizarTipoTransacao(item.tipo_transacao ?? item.tipoTransacao) ??
    normalizarTipoTransacao(tipoCatalogo?.tipo_transacao ?? tipoCatalogo?.tipoTransacao ?? tipoCatalogo?.tipo);

  const banco = item.banco;
  const parcela = item.parcela;
  const fonte = item.fontePagamento || item.fonte;
  const pagamentos = normalizePagamentosAdiantamentoFromApi(item);
  const totais = calcularTotaisPagamentoAdiantamento(valor, pagamentos, Number(catalogId) === ID_TIPO_DEVOLUCAO_GUIA);

  const tipoNome = tipoCatalogo?.nome ?? item.tipo_nome ?? item.tipoNome ?? null;

  return {
    ...item,
    adiantamento_reembolso_tipo_id: catalogId,
    tipo: tipoRaw,
    tipo_transacao: tipoTransacao,
    tipo_nome: tipoNome,
    valor,
    parceiro_id: parceiroId,
    pagamentos,
    totalPago: totais.totalPago,
    faltaPagar: totais.faltaPagar,
  };
}

function hasPagamentoFornecedorInformado(pagamento) {
  const formaPagamentoValue = pagamento?.formaPagamento?.value;
  const valorNumerico = toNumber(pagamento?.valor ?? 0);
  return Boolean(formaPagamentoValue && Number(formaPagamentoValue) > 0 && valorNumerico > 0);
}

function getTaxaMaquininhaPercentual(pagamento) {
  const snapshotRaw =
    pagamento?.taxa_maquininha_snapshot ?? pagamento?.taxaMaquininhaSnapshot;
  const snapshot =
    snapshotRaw && typeof snapshotRaw === "string"
      ? (() => {
          try {
            const parsed = JSON.parse(snapshotRaw);
            return parsed && typeof parsed === "object" ? parsed : null;
          } catch {
            return null;
          }
        })()
      : snapshotRaw && typeof snapshotRaw === "object"
      ? snapshotRaw
      : null;

  if (snapshot) {
    const ehCreditoSnapshot = snapshot?.pag_credito === "SIM";
    const parcelaSnapshot = Number(pagamento?.parcela?.value ?? 1);
    const taxaParceladoSnapshot = snapshot?.pag_taxa_parcelado;
    const tabelaParceladoSnapshot = (() => {
      if (taxaParceladoSnapshot == null || taxaParceladoSnapshot === "") return null;
      if (Array.isArray(taxaParceladoSnapshot)) return taxaParceladoSnapshot;
      if (typeof taxaParceladoSnapshot === "string") {
        try {
          const parsed = JSON.parse(taxaParceladoSnapshot);
          return Array.isArray(parsed) ? parsed : null;
        } catch {
          return null;
        }
      }
      return null;
    })();

    if (ehCreditoSnapshot && parcelaSnapshot > 1) {
      if (Array.isArray(tabelaParceladoSnapshot) && tabelaParceladoSnapshot.length > 0) {
        const taxaExata = tabelaParceladoSnapshot.find(
          (item) => Number(item?.parcela) === parcelaSnapshot
        );
        if (taxaExata) return toNumber(taxaExata?.taxa);

        const taxasAteParcela = tabelaParceladoSnapshot
          .filter(
            (item) =>
              Number(item?.parcela) > 1 && Number(item?.parcela) <= parcelaSnapshot
          )
          .sort((a, b) => Number(b?.parcela) - Number(a?.parcela));
        if (taxasAteParcela.length > 0) return toNumber(taxasAteParcela[0]?.taxa);
      }

      return toNumber(taxaParceladoSnapshot);
    }

    return toNumber(snapshot?.pag_taxa);
  }

  const forma = pagamento?.formaPagamento || {};
  const ehCredito = forma?.pag_credito === "SIM";
  const parcela = Number(pagamento?.parcela?.value ?? 1);
  const taxaParceladoRaw = forma?.pag_taxa_parcelado;

  const taxaParceladaPorParcela = (() => {
    if (taxaParceladoRaw == null || taxaParceladoRaw === "") return null;
    if (Array.isArray(taxaParceladoRaw)) return taxaParceladoRaw;
    if (typeof taxaParceladoRaw === "string") {
      try {
        const parsed = JSON.parse(taxaParceladoRaw);
        return Array.isArray(parsed) ? parsed : null;
      } catch {
        return null;
      }
    }
    return null;
  })();

  if (ehCredito && parcela > 1) {
    if (Array.isArray(taxaParceladaPorParcela) && taxaParceladaPorParcela.length > 0) {
      const taxaExata = taxaParceladaPorParcela.find(
        (item) => Number(item?.parcela) === parcela
      );
      if (taxaExata) return toNumber(taxaExata?.taxa);

      const taxasAteParcela = taxaParceladaPorParcela
        .filter((item) => Number(item?.parcela) > 1 && Number(item?.parcela) <= parcela)
        .sort((a, b) => Number(b?.parcela) - Number(a?.parcela));

      if (taxasAteParcela.length > 0) return toNumber(taxasAteParcela[0]?.taxa);
    }

    return toNumber(taxaParceladoRaw);
  }

  return toNumber(forma?.pag_taxa);
}

function acumularTaxaMaquininhaPagamento(pagamento, taxasPorFornecedor, fornecedorIdLinha = null) {
  if (pagamentoEhPagamentoDaPropriaTaxaMaquininha(pagamento, fornecedorIdLinha)) return;

  const forma = pagamento?.formaPagamento || {};
  const fornecedorIdTaxa = Number(forma?.pag_fornecedor_id || 0);
  const taxaPercentual = getTaxaMaquininhaPercentual(pagamento);
  const valorPagamento = toNumber(pagamento?.valor ?? pagamento?.total);

  if (fornecedorIdTaxa <= 0 || taxaPercentual <= 0 || valorPagamento <= 0) return;

  const taxaCalculada = round2(valorPagamento * (taxaPercentual / 100));
  if (taxaCalculada <= 0) return;

  const nomeTaxa =
    (forma?.pag_fornecedor_nome && String(forma.pag_fornecedor_nome).trim()) || null;

  if (!taxasPorFornecedor[fornecedorIdTaxa]) {
    taxasPorFornecedor[fornecedorIdTaxa] = {
      fornecedorId: fornecedorIdTaxa,
      fornecedorNome: nomeTaxa,
      totalTaxa: 0,
    };
  } else if (nomeTaxa) {
    taxasPorFornecedor[fornecedorIdTaxa].fornecedorNome = nomeTaxa;
  }

  taxasPorFornecedor[fornecedorIdTaxa].totalTaxa = round2(
    taxasPorFornecedor[fornecedorIdTaxa].totalTaxa + taxaCalculada
  );
}

function calcularTaxasDespesaPorFornecedor(financeiros = [], fornecedores = []) {
  const taxasPorFornecedor = {};

  // 1) Pagamentos da reserva (antecipado + embarque): já trazem pag_fornecedor_id na forma.
  const pagamentosReserva = [];
  for (const financeiro of financeiros || []) {
    pagamentosReserva.push(...(financeiro?.pagamentosAntecipado || []));
    pagamentosReserva.push(...(financeiro?.pagamentosEmbarque || []));
  }
  for (const pagamento of pagamentosReserva) {
    acumularTaxaMaquininhaPagamento(pagamento, taxasPorFornecedor);
  }

  // 2) Pagamentos das linhas de despesa: o backend agora envia pag_taxa e pag_fornecedor_id na forma.
  for (const fornecedor of fornecedores || []) {
    const fornecedorIdLinha = Number(fornecedor?.fornecedor?.value || 0);
    const pagamentosFornecedor = fornecedor?.pagamentos || [];
    for (const pagamento of pagamentosFornecedor) {
      if (!hasPagamentoFornecedorInformado(pagamento)) continue;
      acumularTaxaMaquininhaPagamento(pagamento, taxasPorFornecedor, fornecedorIdLinha);
    }
  }

  return Object.values(taxasPorFornecedor);
}

function criarLinhaDespesaTaxaMaquininhaAutomatica(taxa) {
  const id = Number(taxa?.fornecedorId || 0);
  const nomeTaxa = taxa?.fornecedorNome?.trim() || null;
  const label = nomeTaxa || `Fornecedor ${id}`;
  return {
    fornecedor: { label, value: id },
    tipoDespesa: { label: "Por Serviço", value: "POR_SERVICO" },
    despesa_tipo: "POR_SERVICO",
    cobranca: "SERVICO",
    descricao: "Taxa maquininha",
    pagamentos: [
      {
        valor: "0,00",
        fontePagamento: { label: "Selecione a fonte de pagamento!", value: "" },
        formaPagamento: { label: "Selecione a forma de pagamento!", value: "" },
        parcela: { label: "1X", value: 1 },
        banco: { label: "Selecione um banco!", value: 0 },
        local: "",
      },
    ],
    tipos: [],
    aReceber: "0.00",
    faltaPagar: 0,
    qtd_servico: 1,
    valorServico: 0,
    valorServicoInicialBanco: 0,
    qtd_adt: 0,
    valorADT: "0.00",
    qtd_chd: 0,
    valorCHD: "0.00",
    qtd_inf: 0,
    valorINF: "0.00",
    qtd_jovem: 0,
    valorJVM: "0.00",
    qtd_idoso: 0,
    valorIDSO: "0.00",
    total: 0,
    totalPagoForncedor: 0,
    totalPago: 0,
    formaPagamento: { label: "Selecione a forma de pagamento!", value: 0 },
    valorNet: null,
    valorPago: 0,
    acertoFornecedorId: null,
    isNovaLinha: true,
    guia: false,
    isDespesaTaxaMaquininhaGerada: true,
    diariaCategoriaId: null,
    diariaCategoria: null,
  };
}

/** Inclui linha de despesa do fornecedor da maquininha (cadastro) quando a taxa existe mas não há linha. */
function garantirLinhasDespesaTaxaMaquininha(fornecedores = [], taxasPorFornecedor = []) {
  const lista = [...(fornecedores || [])];
  const idsPresentes = new Set(
    lista
      .map((f) => Number(f?.fornecedor?.value || 0))
      .filter((id) => id > 0)
  );

  for (const taxa of taxasPorFornecedor || []) {
    const id = Number(taxa?.fornecedorId || 0);
    const total = round2(toNumber(taxa?.totalTaxa));
    if (id <= 0 || total <= 0) continue;
    if (idsPresentes.has(id)) continue;
    idsPresentes.add(id);
    lista.push(criarLinhaDespesaTaxaMaquininhaAutomatica(taxa));
  }
  return lista;
}

function removerLinhasTaxaMaquininhaGeradaSemTaxa(fornecedores = [], taxasPorFornecedor = []) {
  const comTaxa = new Set();
  for (const t of taxasPorFornecedor || []) {
    const id = Number(t?.fornecedorId || 0);
    if (id > 0 && round2(toNumber(t?.totalTaxa)) > 0) {
      comTaxa.add(id);
    }
  }
  return (fornecedores || []).filter((f) => {
    if (!f?.isDespesaTaxaMaquininhaGerada) return true;
    const id = Number(f?.fornecedor?.value || 0);
    return comTaxa.has(id);
  });
}

function aplicarTaxaMaquininhaEmFornecedores(fornecedores, financeiros) {
  const taxas = calcularTaxasDespesaPorFornecedor(financeiros, fornecedores);
  let lista = garantirLinhasDespesaTaxaMaquininha(fornecedores, taxas);
  lista = montarDespesasTaxaMaquininha(lista, taxas);
  lista = removerLinhasTaxaMaquininhaGeradaSemTaxa(lista, taxas);
  return ordenarLinhasDespesa(lista);
}

/** Guia → demais despesas → fornecedores do passeio → taxa maquininha. */
function ordenarLinhasDespesa(fornecedores = []) {
  const guia = [];
  const demais = [];
  const fornecedoresPasseio = [];
  const taxaMaquininha = [];

  for (const row of fornecedores || []) {
    if (row?.isDespesaTaxaMaquininhaGerada || row?.isDespesaTaxaMaquininha) {
      taxaMaquininha.push(row);
      continue;
    }
    if (row?.guia) {
      guia.push(row);
      continue;
    }
    if (row?.origemPasseio) {
      fornecedoresPasseio.push(row);
      continue;
    }
    demais.push(row);
  }

  const compararNome = (a, b) => String(a?.fornecedor?.label || '').localeCompare(
    String(b?.fornecedor?.label || ''),
    'pt-BR',
    { sensitivity: 'base' }
  );

  const compararGuia = (a, b) => {
    const tipoA = String(a?.diariaTipo || 'COMUM').toUpperCase();
    const tipoB = String(b?.diariaTipo || 'COMUM').toUpperCase();
    if (tipoA !== tipoB) return tipoA === 'COMUM' ? -1 : 1;
    return compararNome(a, b);
  };

  guia.sort(compararGuia);
  demais.sort(compararNome);
  fornecedoresPasseio.sort(compararNome);
  taxaMaquininha.sort(compararNome);

  return [...guia, ...demais, ...fornecedoresPasseio, ...taxaMaquininha];
}

function montarDespesasTaxaMaquininha(fornecedores = [], taxasPorFornecedor = []) {
  const fornecedoresBase = (fornecedores || []).filter(
    (fornecedor) => !fornecedor?.isDespesaTaxaMaquininha
  );

  const taxaPorFornecedorId = {};
  for (const taxa of taxasPorFornecedor || []) {
    const id = Number(taxa?.fornecedorId || 0);
    const totalTaxa = round2(toNumber(taxa?.totalTaxa));
    if (id > 0) taxaPorFornecedorId[id] = totalTaxa;
  }

  return fornecedoresBase.map((fornecedor) => {
    const fornecedorId = Number(fornecedor?.fornecedor?.value || 0);
    const taxaAtual = round2(toNumber(taxaPorFornecedorId[fornecedorId] || 0));
    const valorServicoDoBanco = round2(
      toNumber(
        fornecedor?.valorServicoInicialBanco ??
          fornecedor?.valorServico ??
          fornecedor?.valor ??
          0
      )
    );
    const valorServicoAtual = round2(toNumber(fornecedor?.valorServico || 0));

    // Primeira sync: mantém valorServico igual ao inicial (V0). Congela valorServicoBase = V0 - T0
    // e taxaMaquininhaValor = T0. Depois: valorServico = base + taxaAtual (= V0 + (Tn - T0)),
    // nunca base + (Tn - T0) com base = V0-T0 (isso dava V0 + Tn - 2*T0).
    const primeiraSincronizacao =
      (fornecedor?.valorServicoBase === undefined || fornecedor?.valorServicoBase === null) &&
      (fornecedor?.taxaMaquininhaValor === undefined || fornecedor?.taxaMaquininhaValor === null);

    const taxaReferencia = round2(toNumber(fornecedor?.taxaMaquininhaValor || 0));

    let valorServicoBase;
    let deltaTaxa;
    let novoValorServico;

    if (primeiraSincronizacao) {
      valorServicoBase = round2(Math.max(0, valorServicoDoBanco - taxaAtual));
      deltaTaxa = 0;
    } else {
      valorServicoBase = round2(toNumber(fornecedor?.valorServicoBase || 0));
      deltaTaxa = round2(taxaAtual - taxaReferencia);
    }
    novoValorServico = round2(Math.max(0, valorServicoBase + taxaAtual));

    const fornecedorAtualizado = { ...fornecedor };
    fornecedorAtualizado.taxaMaquininhaValor = taxaAtual;
    fornecedorAtualizado.valorServicoBase = valorServicoBase;
    fornecedorAtualizado.valorServico = novoValorServico;

    if (fornecedorAtualizado.cobranca === "SERVICO") {
      if (Object.hasOwn(fornecedorAtualizado, "guia")) {
        fornecedorAtualizado.total =
          Number(fornecedorAtualizado.valorServico || 0) *
          Number(fornecedorAtualizado.qtd_servico || 0);
      } else {
        fornecedorAtualizado.total = Number(fornecedorAtualizado.valorServico || 0);
      }
    } else {
      fornecedorAtualizado.total = round2(toNumber(fornecedorAtualizado.total || 0) + deltaTaxa);
    }

    let totalPago = 0;
    for (const pagamento of fornecedorAtualizado?.pagamentos || []) {
      if (pagamento?.formaPagamento?.value !== 1) {
        totalPago += valorPagamentoComTaxa(pagamento, fornecedorId);
      }
    }
    totalPago = round2(totalPago);
    fornecedorAtualizado.totalPago = totalPago;
    fornecedorAtualizado.faltaPagar = round2(
      Math.max(0, toNumber(fornecedorAtualizado.total || 0) - totalPago)
    );



    return fornecedorAtualizado;
  });
}

const INITIAL_STATE = {
  totais: {
    total: 0,
    valorPago: 0,
  },
  totaisRecebimentos: {
    total: 0,
    totalRecebidos: 0,
    valorCHD: 0,
    valorADT: 0,
    valorINF: 0,
    valorJVM: 0,
    valorIDS: 0,
  },
  pagamentos: {
    totalDinheiro: 0,
    totalCredito: 0,
    totalCartaoPax: 0,
    totalDebitoConta: 0,
  },
  lucroBruto: {
    totalVendas: 0,
    totalCusto: 0,
    totalEncargos: 0,
    lucroBruto: 0,
  },
  // CLIENTES
  financeiros: [],

  // FORNECEDORES
  fornecedores: [],
  fornecedoresTotais: {
    total: 0,
    valorPago: 0,
    faltaPagar: 0,
  },
  fornecedor: {
    parceiro: {},
    pagamentos: [],
  },
  adiantamentoModal: {
    index: null,
    valor: "0",
    pagamentos: [],
    totalPago: 0,
    faltaPagar: 0,
  },

  // CARDS
  recebimento: {
    dinheiro: 0,
    cartaoCredito: 0,
    cartaoDebito: 0,
    pix: 0,
    cartaoFornecedor: 0,
    taxas: 0,
    total: 0,
  },
  pagamento: {
    cash: 0,
    pax: 0,
    cartaoCredito: 0,
    cartaoDebito: 0,
    pix: 0,
    faturado: 0,
    desconto: 0,
    total: 0,
  },
  resumo: {
    cash: 0,
    comissao: 0,
    faturado: 0,
    aPagar: 0,
    pagoFornecedor: 0,
  },
  saldoFinanceiro: { totalNet: 0, totalCusto: 0, total: 0 },
  // Repasses
  repassesPendentes: [],
  repassesAprovados: [],
  // Devoluções — contagens para badge/alerta (sem filtros)
  devolucoesContagens: {
    PENDENTE: 0,
    PENDENTE_PAX: 0,
  },
  recebimentos: [],
  totaisFinanceiros: {
    dinheiro: 0,
    receber: 0,
    pagar: 0,
    pagoFornecedor: 0,
    totalEncargos: 0,
    faturado: 0,
  },
  acertos: [],
  acertosPagination: {
    current_page: 1,
    last_page: 1,
    total: 0,
  },
  acertosContadores: {
    PENDENTE: 0,
    CONFIRMADO: 0,
  },
  acerto: {},
  adiantamentosReembolsos: [],
  categoriasDiariaPasseio: [],
  modulo: "cadastro",
  dadosFiltro: {
    filtroDataDe: "",
    filtroDataAte: "",
    filtroGuia: "",
    filtroUsuario: "",
    filtroTour: "",
    filtroPasseio: "",
    filtroDataConferencia: "",
  },
  activeTab: 1,
};


export default (state = INITIAL_STATE, action) => {
  switch (action.type) {


    case "FINANCEIROS_ATUALIZAR_VALORES_PAX_SUCESSO": {

      const reservaAtualizada = action.payload;

      const financeirosAtualizados = state.financeiros.map((reserva) =>
        reserva.res_id === reservaAtualizada.res_id
          ? reservaAtualizada
          : reserva
      );

      const newTotaisRecebimentos = {
        total: 0,
        totalRecebidos: 0,
        valorCHD: 0,
        valorADT: 0,
        valorINF: 0,
        valorJVM: 0,
        valorIDS: 0,
      };

      for (const item of financeirosAtualizados) {
        newTotaisRecebimentos.total += Number(item.total_neto_com_taxa || 0);
        newTotaisRecebimentos.valorCHD += Number(item.res_qtd_chd || 0);
        newTotaisRecebimentos.valorADT += Number(item.res_qtd_adt || 0);
        newTotaisRecebimentos.valorINF += Number(item.res_qtd_ifm || 0);
        newTotaisRecebimentos.valorJVM += Number(item.res_qtd_jvm || 0);
        newTotaisRecebimentos.valorIDS += Number(item.res_qtd_ids || 0);
        newTotaisRecebimentos.totalRecebidos += Number(
          item.valor_total_embarque || 0
        );
      }

      return {
        ...state,
        financeiros: financeirosAtualizados,
        totaisRecebimentos: newTotais,
      };
    }



    case "FINANCEIROS_FETCH":
      var newTotais = { ...state.totaisRecebimentos };
      var totaisFinanceirosCopy = { ...state.totaisFinanceiros };
      var financeirosCopy = [...action.payload];

      newTotais.valorCHD = 0;
      newTotais.valorADT = 0;
      newTotais.valorINF = 0;
      newTotais.valorJVM = 0;
      newTotais.valorIDS = 0;


      newTotais.totalRecebidos = 0;
      newTotais.total = 0;
      var resumoCopy = { faturado: 0 };
      for (const item of financeirosCopy) {
        item.total_a_receber = aReceberNoAcerto(item);
        item.pagamentosAntecipado = item.pagamentos;

        item.pagamentosEmbarque = [];

        for (const pagamento of item.pagamentosAntecipado) {
          pagamento.formaPagamento = montarFormaPagamentoOptionFromApi(
            pagamento.forma,
            pagamento.pag_idpagamento
          );
          pagamento.tipoPagamento = montarTipoPagamentoFromForma(pagamento.formaPagamento);
          pagamento.valor = pagamento.pag_valor;
          pagamento.fontePagamento = {
            label: "Selecione a fonte de pagamento!",
            value: 0,
          };
          pagamento.comprovantesArquivos = normalizarComprovantesPagamento(
            pagamento.comprovantes
          );
            (pagamento.banco = { label: "Selecione o banco!", value: 0 }),
            (pagamento.parcela = { label: "1X", value: 1 });
          pagamento.taxaCliente = montarTaxaClienteOptionFromPagamento(pagamento);
          pagamento.taxa_cliente_snapshot = pagamento.taxa_cliente_snapshot || pagamento.taxaClienteSnapshot || null;
          // pag_idtipo == 6 é para FATURADO
          if (pagamento.formaPagamento.pag_idtipo == 6) {
            resumoCopy.faturado =
              resumoCopy.faturado + Number(pagamento.pag_valor);
          }
        }

        if (
          item.pagamentosEmbarque.length <= 0 &&
          item.acerto_reserva &&
          item.acerto_reserva.acerto_reserva_pagamentos &&
          item.acerto_reserva.acerto_reserva_pagamentos.length > 0
        ) {
          for (const pagamento of item.acerto_reserva
            .acerto_reserva_pagamentos) {
            item.pagamentosEmbarque.push({
              banco:
                pagamento?.banco !== null
                  ? {
                    label: pagamento.banco.ban_nome,
                    value: pagamento.pag_idbanco,
                  }
                  : { label: "Selecione o banco!", value: null },
              valor: pagamento?.valor > 0 ? pagamento?.valor : 0,
              formaPagamento: montarFormaPagamentoOptionFromApi(
                pagamento?.forma,
                pagamento?.forma?.pag_id
              ),
              tipoPagamento: montarTipoPagamentoFromForma({
                pag_idtipo: pagamento?.forma?.pag_idtipo,
                tipo_nome: pagamento?.forma?.tipo?.nome,
                tipo: pagamento?.forma?.tipo,
              }),
              fontePagamento: {
                label: pagamento?.fonte?.nome,
                value: pagamento?.fonte?.id,
              },
              parcela:
                pagamento.parcela !== null
                  ? {
                    value: pagamento?.parcela?.par_id,
                    label: pagamento?.parcela?.par_nome,
                  }
                  : { label: "1X", value: 1 },
              local: pagamento?.pag_local,
              motivoCortesia: pagamento?.motivo_cortesia_id
                ? {
                  value: pagamento.motivo_cortesia_id,
                  label: pagamento?.motivo_cortesia?.nome ?? "",
                }
                : null,
              cortesiaResponsavel: pagamento?.cortesia_responsavel
                ? {
                  value: pagamento.cortesia_responsavel,
                  label: pagamento.cortesia_responsavel,
                }
                : null,
              id: pagamento?.acerto_reserva_id,
              comprovantesArquivos: normalizarComprovantesPagamento(pagamento?.comprovantes),
              taxaCliente: montarTaxaClienteOptionFromPagamento(pagamento),
              taxa_cliente_snapshot: pagamento?.taxa_cliente_snapshot || pagamento?.taxaClienteSnapshot || null,
            });
          }
        }

        if (item.pagamentosEmbarque.length <= 0) {
          item.pagamentosEmbarque = [
            {
              valor: item.total_a_receber,
              fontePagamento: {
                label: "Selecione a fonte de pagamento!",
                value: 0,
              },
              formaPagamento: {
                label: "Selecione a forma de pagamento",
                pag_credito: null,
                pag_cash: null,
                pag_debito: null,
                value: 0,
              },
              banco: { label: "Selecione o banco!", value: 0 },
              parcela: { label: "1X", value: 1 },
            },
          ];
        }

        item.res_total_venda =
          item.res_valor_pax * item.res_qtd_adt +
          item.res_valor_chd * item.res_qtd_chd +
          item.res_valor_ids * item.res_qtd_ids +
          item.res_valor_inf * item.res_qtd_ifm +
          item.res_valor_jvm * item.res_qtd_jvm;

        newTotais.total += Number(item.total_neto_com_taxa || 0);
        // Somar taxas de cartão dos pagamentos antecipados no total das vendas
        for (const pagamento of item.pagamentosAntecipado || []) {
          const valor = +String(pagamento?.valor ?? 0).replace(",", ".");
          newTotais.total += valor * (percentualTaxaClienteDoPagamento(pagamento) / 100);
        }
        // Somar taxas de cartão dos pagamentos no embarque no total das vendas
        for (const pagamento of item.pagamentosEmbarque || []) {
          const valor = +String(pagamento?.valor ?? 0).replace(",", ".");
          newTotais.total += valor * (percentualTaxaClienteDoPagamento(pagamento) / 100);
        }
        newTotais.valorCHD += Number(item.res_qtd_chd || 0);
        newTotais.valorADT += Number(item.res_qtd_adt || 0);
        newTotais.valorINF += Number(item.res_qtd_ifm || 0);
        newTotais.valorJVM += Number(item.res_qtd_jvm || 0);
        newTotais.valorIDS += Number(item.res_qtd_ids || 0);

        newTotais.totalRecebidos += item.total_a_receber;
      }

      const fornecedoresComTaxaMaquininha = aplicarTaxaMaquininhaEmFornecedores(
        state.fornecedores,
        financeirosCopy
      );

      return {
        ...state,
        financeiros: financeirosCopy,
        fornecedores: fornecedoresComTaxaMaquininha,
        totaisRecebimentos: newTotais,
        totaisFinanceiros: totaisFinanceirosCopy,
        resumo: resumoCopy,
      };
    case "FORNECEDORES_FETCH": {
      var fornecedoresCopy = [...action.payload];

      for (const fornecedor of fornecedoresCopy) {
        var totalPago = 0;

        const totalPagoForncedor = 0;
        if (!fornecedor.pagamentos || fornecedor.pagamentos.length < 1) {
          fornecedor.pagamentos = [];
          fornecedor.pagamentos.banco = {
            label: "Selecione o banco!",
            value: 0,
          };
          fornecedor.pagamentos.parcela = { label: "1X", value: 1 };
        } else {
          fornecedor.pagamentos = fornecedor.pagamentos.map((pagamento) =>
            normalizarPagamentoFornecedor(pagamento)
          );
        }
        const fornecedorIdLinha = Number(fornecedor?.fornecedor?.value || 0);
        for (const pagamento of fornecedor?.pagamentos) {
          // Não somar valores com forma de pagamento "FATURADO" (ID 1)
          if (pagamento?.formaPagamento?.value !== 1) {
            totalPago = totalPago + valorPagamentoComTaxa(pagamento, fornecedorIdLinha);
          }
        }

        if (fornecedor.parceiro_tarifario_id) {
          let tipo = fornecedor.tipos.filter(
            (item) => item.tar_id == fornecedor.parceiro_tarifario_id
          );

          fornecedor.tipoDespesa.label = tipo[0].produto;
          fornecedor.tipoDespesa.value = tipo[0].tar_id;
          fornecedor.tipoDespesa.precos = tipo[0];
        }
        fornecedor.totalPago = totalPago;
        fornecedor.totalPagoForncedor = totalPagoForncedor;
        // Não definir taxaMaquininhaValor como 0 aqui: isso quebrava a 1ª sincronização real ao editar
        // pagamento (primeiraSincronizacao ficava só por valorServicoBase e congelava valorServico).
        const rawValorDb = fornecedor.valorServico ?? fornecedor.valor;
        fornecedor.valorServicoInicialBanco = round2(toNumber(rawValorDb ?? 0));
      }
      // Preservar linhas locais que ainda não foram salvas (sem acertoFornecedorId)
      // const linhasLocais = state.fornecedores.filter(fornecedor => !fornecedor.acertoFornecedorId);

      // Mesclar dados do backend com linhas locais
      // const fornecedoresMesclados = [...fornecedoresCopy, ...linhasLocais];

      const fornecedoresAposTaxa = aplicarTaxaMaquininhaEmFornecedores(
        fornecedoresCopy,
        state.financeiros
      );

      return { ...state, fornecedores: fornecedoresAposTaxa };
    }
    case "FORNECEDOR_PAGAMENTOS_SET": {
      const forncedorCopy = { ...state.fornecedor };
      forncedorCopy.pagamentos = action.payload;
      return { ...state, fornecedor: forncedorCopy };
    }
    case "FORNECEDOR_SET": {
      const forncedorCopy = { ...state.fornecedor };

      if (forncedorCopy?.pagamentos.length <= 0) {
        forncedorCopy.pagamentos = [
          {
            valor: Number(forncedorCopy.total),
            falta: Number(forncedorCopy.faltaPagar),
            formaPagamento: {
              label: "Selecione a forma de pagamento",
              value: 0,
            },
            fontePagamento: {
              label: "Selecione a fonte de pagamento!",
              value: 0,
            },
            banco: { label: "Selecione o banco!", value: 0 },
            parcela: { label: "1X", value: 1 },
          },
        ];
      }

      return { ...state, fornecedor: forncedorCopy };
    }
    case "ACERTO_FORNECEDOR_SET": {
      var fornecedorCopy = { ...action.payload };
      if (!fornecedorCopy.pagamentos) {
        fornecedorCopy.pagamentos = [];
      }
      return { ...state, fornecedor: fornecedorCopy };
    }
    case "ACERTO_FORNECEDOR_PAGAMENTO_CHANGE": {
      var fornecedorCopy = { ...state.fornecedor };
      var pagamentosCopy = [...state.fornecedor.pagamentos];
      const rowCopy = { ...pagamentosCopy[action.index] };
      if (action.campoChange == "valor") {
        const raw = action.payload != null ? String(action.payload) : "";
        rowCopy[action.campoChange] = raw.replace(",", ".");
      } else if (action.campoChange == "formaPagamento") {
        rowCopy.formaPagamento = action.payload;
        delete rowCopy.taxa_maquininha_snapshot;
        delete rowCopy.taxaMaquininhaSnapshot;
        if (action.payload && action.payload.pag_credito === "SIM") {
          rowCopy.parcela = rowCopy.parcela || { label: "1X", value: 1 };
        }
        if (action.payload && action.payload.value !== 4) {
          rowCopy.banco = { label: "Selecione um banco!", value: 0 };
        }
      } else {
        rowCopy[action.campoChange] = action.payload;
        if (action.campoChange === "parcela") {
          delete rowCopy.taxa_maquininha_snapshot;
          delete rowCopy.taxaMaquininhaSnapshot;
        }
      }
      pagamentosCopy[action.index] = rowCopy;
      fornecedorCopy.pagamentos = pagamentosCopy;
      
      // Recalcular totalPago em tempo real
      const fornecedorIdLinhaModal = Number(fornecedorCopy?.fornecedor?.value || 0);
      var totalPagoCalculado = 0;
      for (const pagamento of pagamentosCopy) {
        if (pagamento?.formaPagamento?.value !== 1) {
          totalPagoCalculado = totalPagoCalculado + valorPagamentoComTaxa(
            pagamento,
            fornecedorIdLinhaModal
          );
        }
      }
      fornecedorCopy.totalPago = totalPagoCalculado;
      fornecedorCopy.faltaPagar = fornecedorCopy.total > totalPagoCalculado 
        ? fornecedorCopy.total - totalPagoCalculado 
        : 0;
      
      return { ...state, fornecedor: fornecedorCopy };
    }
    case "ADD_NEW_DESPESA":
      var newFornecedor = {
        fornecedor: { label: "Selecione o fornecedor!", value: "" },
        tipoDespesa: { label: "Selecione o tipo despesa!", value: null },
        despesa_tipo: null,
        pagamentos: [
          {
            valor: "0,00",
            fontePagamento: { label: "Selecione a fonte de pagamento!", value: "" },
            formaPagamento: { label: "Selecione a forma de pagamento!", value: "" },
            parcela: { label: "1X", value: 1 },
            banco: { label: "Selecione um banco!", value: 0 },
            local: "",
          },
        ],
        tipos: [],
        aReceber: "0.00",
        cobranca: "",
        descricao: "",
        faltaPagar: 0,
        qtd_servico: 0,
        valorServico: 0,
        qtd_adt: 0,
        valorADT: "0.00",
        qtd_chd: 0,
        valorCHD: "0.00",
        qtd_inf: 0,
        valorINF: "0.00",
        qtd_jovem: 0,
        valorJVM: "0.00",
        qtd_idoso: 0,
        valorIDSO: "0.00",
        total: 0,
        totalPagoForncedor: 0,
        totalPago: 0,
        formaPagamento: { label: "Selecione a forma de pagamento!", value: 0 },
        valorNet: null,
        valorPago: 0,
        acertoFornecedorId: null,
        isNovaLinha: true,
        diariaCategoriaId: null,
        diariaCategoria: null,
      };

      var novoArrayDeFornecedores = [...state.fornecedores, newFornecedor];
      return { ...state, fornecedores: novoArrayDeFornecedores };
    case "REMOVER_LINHA_DESPESA":
      var newFornecedor = state.fornecedores.filter(
        (fornecedor, index) => index !== action.index
      );
      const fornecedoresComTaxaAoRemover = aplicarTaxaMaquininhaEmFornecedores(
        newFornecedor,
        state.financeiros
      );

      return { ...state, fornecedores: fornecedoresComTaxaAoRemover };
    case "FINANCEIRO_LUCRO_BRUTO":
      var copyLucroBruto = { ...state.lucroBruto };
      action.payload.map((item) => {
        copyLucroBruto.totalVendas +=
          item.res_valor_pax * item.res_qtd_adt +
          item.res_valor_chd * item.res_qtd_chd +
          item.res_valor_ids * item.res_qtd_ids +
          item.res_valor_inf * item.res_qtd_ifm +
          item.res_valor_jvm * item.res_qtd_jvm;
      });

      return { ...state, lucroBruto: copyLucroBruto };
    case "FORNECEDOR_MUDAR_PROPRIEDADE":
      var copyTotais = { ...state.totais };
      copyTotais.total = 0;
      var copyFornecedores = [...state.fornecedores];
      var fornecedor = { ...copyFornecedores[action.index] };

      // console.log(copyFornecedores, 'bah papo de')

      var camposQuantidade = [
        "qtd_servico",
        "qtd_adt",
        "qtd_chd",
        "qtd_inf",
        "qtd_jovem",
        "qtd_idoso",
      ];

      if (action.propriedade == "valorServico" && isNaN(action.payload)) {
        fornecedor[action.propriedade] = Number(
          action.payload.replace(",", ".")
        );
      } else if (camposQuantidade.indexOf(action.propriedade) !== -1) {
        // Quantidade vazia/inválida vira 0 — assim os totais nunca viram NaN
        // e o backend recebe um inteiro válido para intval().
        var raw = action.payload;
        if (raw === "" || raw === null || raw === undefined) {
          fornecedor[action.propriedade] = 0;
        } else {
          var parsed = Number(raw);
          fornecedor[action.propriedade] = Number.isFinite(parsed) ? parsed : 0;
        }
      } else {
        fornecedor[action.propriedade] = action.payload;
      }
      if (action.propriedade === "valorServico") {
        const valorServicoDigitado = toNumber(fornecedor.valorServico || 0);
        const taxaAtual = toNumber(fornecedor.taxaMaquininhaValor || 0);
        fornecedor.valorServicoBase = round2(Math.max(0, valorServicoDigitado - taxaAtual));
      }
      
      // Marcar o item como editado (apenas se já existia no banco - tem acertoFornecedorId)
      if (fornecedor.acertoFornecedorId) {
        fornecedor.foiEditado = true;
      }

      // console.log(fornecedor, fornecedor.cobranca, fornecedor.qtd_servico, 'bah avail')

      if (fornecedor.cobranca == "PESSOA") {
        const valorADT = typeof fornecedor.valorADT === 'string' ? fornecedor.valorADT.replace(",", ".") : (fornecedor.valorADT || 0);
        const valorCHD = typeof fornecedor.valorCHD === 'string' ? fornecedor.valorCHD.replace(",", ".") : (fornecedor.valorCHD || 0);
        const valorINF = typeof fornecedor.valorINF === 'string' ? fornecedor.valorINF.replace(",", ".") : (fornecedor.valorINF || 0);
        const valorJVM = typeof fornecedor.valorJVM === 'string' ? fornecedor.valorJVM.replace(",", ".") : (fornecedor.valorJVM || 0);
        const valorIDSO = typeof fornecedor.valorIDSO === 'string' ? fornecedor.valorIDSO.replace(",", ".") : (fornecedor.valorIDSO || 0);

        fornecedor.total =
          Number(valorADT) *
          Number(fornecedor.qtd_adt) +
          // + (Number(fornecedor.valorServico) * Number(fornecedor.qtd_servico))
          Number(valorCHD) *
          Number(fornecedor.qtd_chd) +
          Number(valorINF) *
          Number(fornecedor.qtd_inf) +
          Number(valorJVM) *
          Number(fornecedor.qtd_jovem) +
          Number(valorIDSO) *
          Number(fornecedor.qtd_idoso);
      } else if (fornecedor.cobranca == "SERVICO") {
        if (
          fornecedor.qtd_servico == 0 &&
          fornecedor.qtd_adt == 0 &&
          fornecedor.qtd_chd == 0 &&
          fornecedor.qtd_inf == 0 &&
          fornecedor.qtd_jovem == 0 &&
          fornecedor.qtd_idoso == 0
        ) {
          fornecedor.qtd_servico = 1;
        }

        fornecedor.total =
          Number(fornecedor.valorServico) * Number(fornecedor.qtd_servico);
      }

      // console.log(fornecedor.total, 'bah bah')

      copyFornecedores[action.index] = fornecedor;
      copyFornecedores.map((item) => {
        copyTotais.total += item.total;
      });

      // Recalcular faltaPagar baseado no totalPago
      const fornecedorIdLinhaProp = Number(fornecedor?.fornecedor?.value || 0);
      var totalPagoCalculado = 0;
      for (const pagamento of fornecedor?.pagamentos || []) {
        if (pagamento?.formaPagamento?.value !== 1) {
          totalPagoCalculado = totalPagoCalculado + valorPagamentoComTaxa(
            pagamento,
            fornecedorIdLinhaProp
          );
        }
      }
      fornecedor.totalPago = totalPagoCalculado;
      fornecedor.faltaPagar = fornecedor.total - totalPagoCalculado;

      var pagamentosCopy = { ...state.pagamentos };
      var totaisFinanceirosCopy = { ...state.totaisFinanceiros };

      var fornecedorDinheiro = copyFornecedores?.filter(
        (item) => item?.formaPagamento?.pag_cash === "SIM"
      );
      var totalDinheiro = fornecedorDinheiro.reduce(
        (acumulador, objeto) => acumulador + Number(objeto.valorPago),
        0
      );
      pagamentosCopy.totalDinheiro = totalDinheiro;

      var fornecedorDinheiro = copyFornecedores?.filter(
        (item) => item?.formaPagamento?.pag_credito === "SIM"
      );
      var totalCredito = fornecedorDinheiro.reduce(
        (acumulador, objeto) => acumulador + Number(objeto.valorPago),
        0
      );
      pagamentosCopy.totalCredito = totalCredito;

      var fornecedorDinheiro = copyFornecedores?.filter(
        (item) => item?.formaPagamento?.pag_debito === "SIM"
      );
      var totalDebito = fornecedorDinheiro.reduce(
        (acumulador, objeto) => acumulador + Number(objeto.valorPago),
        0
      );
      pagamentosCopy.totalDebitoConta = totalDebito;

      var fornecedorDinheiro = copyFornecedores?.filter(
        (item) => item?.formaPagamento?.value == 10
      );
      var totalCartaoPax = fornecedorDinheiro.reduce(
        (acumulador, objeto) => acumulador + Number(objeto.valorPago),
        0
      );

      pagamentosCopy.totalCartaoPax = totalCartaoPax;
      totaisFinanceirosCopy.pagoFornecedor = totalCartaoPax;

      return {
        ...state,
        fornecedores: copyFornecedores,
        totais: copyTotais,
        pagamentos: pagamentosCopy,
        totaisFinanceiros: totaisFinanceirosCopy,
      };
    case "FORNECEDOR_SET_TARIFARIO_DADOS": {
      var copyFornecedores = [...state.fornecedores];
      var fornecedor = { ...copyFornecedores[action.index] };
      var tarifario = { ...action.payload };

      // console.log(tarifario, 'FORNECEDOR_SET_TARIFARIO_DADOS')

      // if (tarifario.cobranca == 'PESSOA') {
      //     fornecedor.qtd_adt = tarifario.qtd_adt
      //     fornecedor.qtd_chd = tarifario.qtd_chd
      //     fornecedor.qtd_inf = tarifario.qtd_inf
      //     fornecedor.qtd_jovem = tarifario.qtd_jovem
      //     fornecedor.qtd_idoso = tarifario.qtd_idoso
      // } else if (tarifario.cobranca == 'SERVICO') {
      //     fornecedor.qtd_servico = tarifario.qtd_servico
      // }

      fornecedor.cobranca = tarifario?.cobranca;
      copyFornecedores[action.index] = fornecedor;

      return { ...state, fornecedores: copyFornecedores };
    }

    case "FORNECEDOR_MARCAR_TIPOS_CARREGADOS": {
      var copyFornecedores = [...state.fornecedores];
      var fornecedor = { ...copyFornecedores[action.index] };
      fornecedor.tiposCarregados = true;
      copyFornecedores[action.index] = fornecedor;
      return { ...state, fornecedores: copyFornecedores };
    }

    case "FORNECEDOR_LIMPAR_TIPOS_CARREGADOS": {
      var copyFornecedores = [...state.fornecedores];
      var fornecedor = { ...copyFornecedores[action.index] };

      // Limpar flag de tipos carregados para permitir carregar novos tipos
      fornecedor.tiposCarregados = false;
      fornecedor.tipos = []; // Limpar tipos antigos
      copyFornecedores[action.index] = fornecedor;

      return { ...state, fornecedores: copyFornecedores };
    }
    case "FORNECEDOR_MUDAR_VALORES":
      var copyTotais = { ...state.totais };
      copyTotais.total = 0;
      var copyFornecedores = [...state.fornecedores];
      var fornecedor = { ...copyFornecedores[action.index] };

      // Verifica se action.payload existe antes de acessar suas propriedades
      if (action.payload) {
        fornecedor.valorADT = action.payload.valor_adt || "0.00";
        fornecedor.valorServico = action.payload.valor_servico || "0.00";
        fornecedor.cobranca = action.payload.cobranca;
        fornecedor.valorCHD = action.payload.valor_chd || "0.00";
        fornecedor.valorINF = action.payload.valor_inf || "0.00";
        fornecedor.valorJVM = action.payload.valor_jvm || "0.00";
        fornecedor.valorIDSO = action.payload.valor_idso || "0.00";
      }

      // CALCULO TOTAL
      if (action.payload && action.payload.cobranca == "PESSOA") {
        // Para cobrança por pessoa, NÃO incluir valorServico
        fornecedor.total =
          Number(fornecedor.valorADT || 0) * Number(fornecedor.qtd_adt || 0) +
          Number(fornecedor.valorCHD || 0) * Number(fornecedor.qtd_chd || 0) +
          Number(fornecedor.valorINF || 0) * Number(fornecedor.qtd_inf || 0) +
          Number(fornecedor.valorJVM || 0) * Number(fornecedor.qtd_jovem || 0) +
          Number(fornecedor.valorIDSO || 0) * Number(fornecedor.qtd_idoso || 0);
      } else if (action.payload && action.payload.cobranca == "SERVICO") {
        if (Object.hasOwn(fornecedor, "guia")) {
          fornecedor.total =
            Number(fornecedor.valorServico || 0) * Number(fornecedor.qtd_servico || 0);
        } else {
          fornecedor.total = Number(fornecedor.valorServico || 0);
        }
      }

      // Calcular total pago excluindo pagamentos "FATURADO" (ID 1), incluindo taxa de cartão
      const fornecedorIdLinhaSelect = Number(fornecedor?.fornecedor?.value || 0);
      var totalPagoCalculado = 0;
      if (fornecedor.pagamentos && fornecedor.pagamentos.length > 0) {
        for (const pagamento of fornecedor.pagamentos) {
          if (pagamento?.formaPagamento?.value !== 1) {
            totalPagoCalculado = totalPagoCalculado + valorPagamentoComTaxa(
              pagamento,
              fornecedorIdLinhaSelect
            );
          }
        }
      }
      fornecedor.faltaPagar = fornecedor.total - totalPagoCalculado;

      copyFornecedores[action.index] = fornecedor;
      copyFornecedores.map((item) => {
        copyTotais.total += item.total;
      });

      return { ...state, fornecedores: copyFornecedores, totais: copyTotais };
    case "FORNECEDOR_LISTA_ATIVIDADES": {
      var copyFornecedores = [...state.fornecedores];
      var fornecedor = { ...copyFornecedores[action.index] };
      var payload = action.payload;
      var atividades = Array.isArray(payload) ? payload : (payload.tipos || []);
      var resetarDespesa = Array.isArray(payload) ? true : (payload.resetarDespesa !== false);
      fornecedor.tipos = atividades;
      if (resetarDespesa) {
        fornecedor.tipoDespesa = { label: "Selecione o tipo despesa!", value: null };
        fornecedor.despesa_tipo = null;
        fornecedor.descricao = "";
        fornecedor.cobranca = "";
        fornecedor.diariaCategoria = null;
        fornecedor.diariaCategoriaId = null;
        fornecedor.valorServico = 0;
        fornecedor.valorADT = 0;
        fornecedor.valorCHD = 0;
        fornecedor.valorINF = 0;
        fornecedor.valorJVM = 0;
        fornecedor.valorIDSO = 0;
        fornecedor.valorServicoPreenchido = false;
        fornecedor.valorADTPreenchido = false;
        fornecedor.valorCHDPreenchido = false;
        fornecedor.valorINFPreenchido = false;
        fornecedor.valorJVMPreenchido = false;
        fornecedor.valorIDSOPreenchido = false;
        fornecedor.total = 0;
      }
      copyFornecedores[action.index] = fornecedor;
      return { ...state, fornecedores: copyFornecedores };
    }
    
    case "FINANCEIROS_RESERVA_PAGAMENTOS_SALVAR":
      var financeirosTCopy = [...state.financeiros];
      var financeiroTCopy = { ...financeirosTCopy[action.index] };
      financeiroTCopy = action.payload;
      if (financeiroTCopy.total_a_receber == null) {
        financeiroTCopy.total_a_receber = Number(
          financeirosTCopy[action.index]?.total_a_receber ??
            financeirosTCopy[action.index]?.valor_total_embarque ??
            0
        );
      }
      financeirosTCopy[action.index] = financeiroTCopy;
      const fornecedoresComTaxaMaquininhaAtualizada = aplicarTaxaMaquininhaEmFornecedores(
        state.fornecedores,
        financeirosTCopy
      );

      return {
        ...state,
        financeiros: financeirosTCopy,
        fornecedores: fornecedoresComTaxaMaquininhaAtualizada,
      };

    case "FINANCEIRO_ATUALIZAR_TAXAS": {
      const reservaAtualizada = action.payload;
      const financeirosTaxasCopy = state.financeiros.map(f => {
        if (f.res_id === reservaAtualizada.res_id) {
          return {
            ...f,
            res_total_neto: reservaAtualizada.res_total_neto,
            valor_total_embarque: reservaAtualizada.valor_total_embarque,
            total_a_receber: aReceberNoAcerto({ ...f, valor_total_embarque: reservaAtualizada.valor_total_embarque }),
            valores_taxas: reservaAtualizada.valores_taxas ?? f.valores_taxas,
          };
        }
        return f;
      });
      return { ...state, financeiros: financeirosTaxasCopy };
    }

    case "FINANCEIRO_ATUALIZAR_RESERVA": {
      const reservaAtualizada = action.payload;
      const financeirosAtualizados = state.financeiros.map((f) => {
        if (f.res_id !== reservaAtualizada.res_id) return f;

        // Reprocessa pagamentos antecipados (vêm crus do banco) no mesmo formato do FINANCEIROS_FETCH
        const pagamentosAntecipadoNovos = (reservaAtualizada.pagamentos || []).map((pagamento) => ({
          ...pagamento,
          formaPagamento: montarFormaPagamentoOptionFromApi(
            pagamento.forma,
            pagamento.pag_idpagamento
          ),
          tipoPagamento: montarTipoPagamentoFromForma({
            pag_idtipo: pagamento.forma?.pag_idtipo,
            tipo_nome: pagamento.forma?.tipo?.nome,
            tipo: pagamento.forma?.tipo,
          }),
          valor: pagamento.pag_valor,
          fontePagamento: { label: "Selecione a fonte de pagamento!", value: 0 },
          comprovantesArquivos: normalizarComprovantesPagamento(pagamento.comprovantes),
          banco: { label: "Selecione o banco!", value: 0 },
          parcela: { label: "1X", value: 1 },
          taxaCliente: montarTaxaClienteOptionFromPagamento(pagamento),
          taxa_cliente_snapshot: pagamento.taxa_cliente_snapshot || pagamento.taxaClienteSnapshot || null,
        }));

        const pagamentosEmbarqueExistentes = f.pagamentosEmbarque || [];
        const usuarioJaPreencheuEmbarque = pagamentosEmbarqueExistentes.some(
          (p) => p?.formaPagamento?.value && Number(p.formaPagamento.value) > 0
        );

        // "Total A Receber" sempre reflete o novo valor da reserva atualizada.
        // Quando o usuário ainda não preencheu pagamentos no embarque, atualiza também
        // o valor da linha default para acompanhar o novo total.
        const aReceber = aReceberNoAcerto({ ...f, ...reservaAtualizada });
        const novoPagamentosEmbarque = (!usuarioJaPreencheuEmbarque && pagamentosEmbarqueExistentes.length === 1)
          ? [{ ...pagamentosEmbarqueExistentes[0], valor: aReceber }]
          : pagamentosEmbarqueExistentes;

        return {
          ...f,
          ...reservaAtualizada,
          pagamentosAntecipado: pagamentosAntecipadoNovos,
          pagamentosEmbarque: novoPagamentosEmbarque,
          valor_total_embarque: reservaAtualizada.valor_total_embarque,
          total_a_receber: aReceber,
        };
      });
      const fornecedoresComTaxaMaquininhaAtualizada = aplicarTaxaMaquininhaEmFornecedores(
        state.fornecedores,
        financeirosAtualizados
      );
      return {
        ...state,
        financeiros: financeirosAtualizados,
        fornecedores: fornecedoresComTaxaMaquininhaAtualizada,
      };
    }

    case "FINANCEIROS_FORNECEDOR_PAGAMENTOS_SALVAR": {
      var fornecedoresCopy = [...state.fornecedores];
      var fornecedorAnterior = { ...fornecedoresCopy[action.index] };
      var fornecedorCopy = { ...fornecedorAnterior, ...action.payload };
      fornecedorCopy.pagamentos = Array.isArray(fornecedorCopy?.pagamentos)
        ? fornecedorCopy.pagamentos.map((pagamento) => normalizarPagamentoFornecedor(pagamento))
        : [];

      const fornecedorIdLinhaSalvar = Number(fornecedorCopy?.fornecedor?.value || 0);
      var totalPago = 0;
      for (const pagamento of fornecedorCopy?.pagamentos) {
        if (pagamento?.formaPagamento?.value !== 1) {
          totalPago = totalPago + valorPagamentoComTaxa(pagamento, fornecedorIdLinhaSalvar);
        }
      }
      var faltaPagar = 0;
      if (totalPago <= fornecedorCopy.total) {
        faltaPagar = fornecedorCopy.total - totalPago;
      } else {
        faltaPagar = 0;
      }
      fornecedorCopy.totalPago = totalPago;
      fornecedorCopy.faltaPagar = faltaPagar;

      fornecedoresCopy[action.index] = fornecedorCopy;
      const fornecedoresComTaxaMaquininhaAtualizada = aplicarTaxaMaquininhaEmFornecedores(
        fornecedoresCopy,
        state.financeiros
      );

      return { ...state, fornecedores: fornecedoresComTaxaMaquininhaAtualizada };
    }

    case "ACERTO_FINANCEIRO_RESULTADOS_SALVAR": {
      // CARDS
      const recebimentoCopy = {
        pix: 0,
        dinheiro: 0,
        cartaoCredito: 0,
        cartaoDebito: 0,
        cartaoFornecedor: 0,
        taxas: 0,
        total: 0,
      };
      var pagamentoCopy = {
        pix: 0,
        pax: 0,
        cash: 0,
        cartaoCredito: 0,
        cartaoDebito: 0,
        faturado: 0,
        desconto: 0,
        total: 0,
      };
      var resumoCopy = {
        pix: 0,
        cash: 0,
        comissao: 0,
        faturado: 0,
        aPagar: 0,
        creditoGerado: 0,
        pagoFornecedor: 0,
      };
      const saldoFinanceiroCopy = { totalNet: 0, totalCusto: 0, total: 0 };

      const fornecedoresTotaisCopy = { total: 0, valorPago: 0, faltaPagar: 0 };
      var fornecedoresCopy = [...state.fornecedores];
      const financeiroCopy = [...state.financeiros];

      // Total Vendas = mesma lógica da coluna Total Net: soma(res_total_neto) + taxas cartão (embarque + antecipado) só quando NÃO é venda direta
      const isVendaDireta = (r) => r?.parceiro?.par_venda_direta === true || r?.parceiro?.par_venda_direta === 1;
      let taxasCartaoEmbarque = 0;
      let taxasCartaoAntecipado = 0;

      for (const reserva of financeiroCopy) {
        resumoCopy.comissao =
          resumoCopy.comissao + Number(reserva?.res_total_comissao);
        const reservaCortesia = reservaTemPagamentoCortesia(reserva);
        const somaTaxaCartaoReserva = !isVendaDireta(reserva) && !reservaCortesia;
        if (reserva.pagamentosAntecipado) {
          for (const pagamento of reserva?.pagamentosAntecipado) {
            const valor = +String(pagamento?.valor ?? 0).replace(",", ".");
            const pagIdTipo = pagamento?.formaPagamento?.pag_idtipo;
            
            // pag_idtipo == 1 é apenas para cash
            if (pagIdTipo == 1) {
              recebimentoCopy.dinheiro = recebimentoCopy.dinheiro + valor;
            }
            // pag_idtipo == 2 é para PIX
            if (pagIdTipo == 2) {
              recebimentoCopy.pix = (recebimentoCopy.pix || 0) + valor;
            }
            // pag_idtipo == 3 é para CRÉDITO
            if (pagIdTipo == 3) {
              recebimentoCopy.cartaoCredito = (recebimentoCopy.cartaoCredito || 0) + valor;
              const taxa = valor * (percentualTaxaClienteDoPagamento(pagamento) / 100);
              recebimentoCopy.taxas = (recebimentoCopy.taxas || 0) + taxa;
              if (somaTaxaCartaoReserva) taxasCartaoAntecipado += taxa;
            }
            // pag_idtipo == 4 é para DÉBITO
            if (pagIdTipo == 4) {
              recebimentoCopy.cartaoDebito = (recebimentoCopy.cartaoDebito || 0) + valor;
            }
            // pag_idtipo == 5 é para CRÉDITO AO FORNECEDOR
            if (pagIdTipo == 5) {
              recebimentoCopy.cartaoFornecedor = (recebimentoCopy.cartaoFornecedor || 0) + valor;
            }
            // pag_idtipo == 6 é para FATURADO
            if (pagIdTipo == 6) {
              resumoCopy.faturado = (resumoCopy.faturado || 0) + valor;
            }
          }
        }
        if (reserva.pagamentosEmbarque) {
          for (const pagamento of reserva?.pagamentosEmbarque) {
            const valor = +String(pagamento?.valor ?? 0).replace(",", ".");
            const pagIdTipo = pagamento?.formaPagamento?.pag_idtipo;
            
            // pag_idtipo == 1 é apenas para cash
            if (pagIdTipo == 1) {
              recebimentoCopy.dinheiro = recebimentoCopy.dinheiro + valor;
            }
            // pag_idtipo == 2 é para PIX
            if (pagIdTipo == 2) {
              recebimentoCopy.pix = (recebimentoCopy.pix || 0) + valor;
            }
            // pag_idtipo == 3 é para CRÉDITO
            if (pagIdTipo == 3) {
              recebimentoCopy.cartaoCredito = (recebimentoCopy.cartaoCredito || 0) + valor;
              const taxa = valor * (percentualTaxaClienteDoPagamento(pagamento) / 100);
              recebimentoCopy.taxas = (recebimentoCopy.taxas || 0) + taxa;
              if (somaTaxaCartaoReserva) taxasCartaoEmbarque += taxa;
            }
            // pag_idtipo == 4 é para DÉBITO
            if (pagIdTipo == 4) {
              recebimentoCopy.cartaoDebito = (recebimentoCopy.cartaoDebito || 0) + valor;
            }
            // pag_idtipo == 5 é para CRÉDITO AO FORNECEDOR
            if (pagIdTipo == 5) {
              recebimentoCopy.cartaoFornecedor = (recebimentoCopy.cartaoFornecedor || 0) + valor;
            }
            // pag_idtipo == 6 é para FATURADO
            if (pagIdTipo == 6) {
              resumoCopy.faturado = (resumoCopy.faturado || 0) + valor;
            }
          }
        }
        // Cortesia (tipos 7 e 9): não soma neto no Total Vendas — só no front.
        if (reserva.res_total_neto && !reservaCortesia) {
          saldoFinanceiroCopy.totalNet =
            saldoFinanceiroCopy.totalNet + Number(reserva.res_total_neto);
        }
      }

      saldoFinanceiroCopy.totalNet = saldoFinanceiroCopy.totalNet + taxasCartaoEmbarque + taxasCartaoAntecipado;

      for (const fornecedor of fornecedoresCopy) {
        var totalPago = 0;
        for (const pagamento of fornecedor?.pagamentos) {
          const pagIdTipo = pagamento?.formaPagamento?.pag_idtipo;
          
          // pag_idtipo == 1 é apenas para cash
          if (pagIdTipo == 1) {
            const valor = +String(pagamento?.valor ?? 0).replace(",", ".");
            pagamentoCopy.cash = pagamentoCopy.cash + valor;
            if (pagamento?.formaPagamento?.value !== 1) {
              totalPago = totalPago + valorPagamentoComTaxa(pagamento);
            }
          }
          // pag_idtipo == 2 é para PIX
          if (pagIdTipo == 2) {
            const valor = +String(pagamento?.valor ?? 0).replace(",", ".");
            pagamentoCopy.pix = (pagamentoCopy.pix || 0) + valor;
            if (pagamento?.formaPagamento?.value !== 1) {
              totalPago = totalPago + valorPagamentoComTaxa(pagamento);
            }
          }
          // pag_idtipo == 3 é para CRÉDITO
          if (pagIdTipo == 3) {
            const valor = +String(pagamento?.valor ?? 0).replace(",", ".");
            pagamentoCopy.cartaoCredito = (pagamentoCopy.cartaoCredito || 0) + valor;
            if (pagamento?.formaPagamento?.value !== 1) {
              totalPago = totalPago + valorPagamentoComTaxa(pagamento);
            }
          }
          // pag_idtipo == 4 é para DÉBITO
          if (pagIdTipo == 4) {
            const valor = +String(pagamento?.valor ?? 0).replace(",", ".");
            pagamentoCopy.cartaoDebito = (pagamentoCopy.cartaoDebito || 0) + valor;
            if (pagamento?.formaPagamento?.value !== 1) {
              totalPago = totalPago + valorPagamentoComTaxa(pagamento);
            }
          }
          // pag_idtipo == 5 é para CRÉDITO AO FORNECEDOR
          if (pagIdTipo == 5) {
            const valor = +String(pagamento?.valor ?? 0).replace(",", ".");
            pagamentoCopy.pax = (pagamentoCopy.pax || 0) + valor;
            if (pagamento?.formaPagamento?.value !== 1) {
              totalPago = totalPago + valorPagamentoComTaxa(pagamento);
            }
          }
          // pag_idtipo == 6 é para FATURADO
          if (pagIdTipo == 6) {
            const valor = +String(pagamento?.valor ?? 0).replace(",", ".");
            pagamentoCopy.faturado = (pagamentoCopy.faturado || 0) + valor;
            // Faturado não conta no totalPago
          }
          // Tipo DESCONTO — forma exclusiva de despesas no acerto
          if (formaPagamentoEhDesconto(pagamento?.formaPagamento)) {
            const valor = +String(pagamento?.valor ?? 0).replace(",", ".");
            pagamentoCopy.desconto = (pagamentoCopy.desconto || 0) + valor;
            if (pagamento?.formaPagamento?.value !== 1) {
              totalPago = totalPago + valor;
            }
          }
        }

        fornecedoresTotaisCopy.valorPago =
          fornecedoresTotaisCopy.valorPago + totalPago;
        if (totalPago <= fornecedor.total) {
          fornecedoresTotaisCopy.faltaPagar =
            fornecedoresTotaisCopy.faltaPagar + (fornecedor.total - totalPago);
          recebimentoCopy.cartaoFornecedor =
            recebimentoCopy.cartaoFornecedor + 0;
        } else {
          recebimentoCopy.cartaoFornecedor =
            recebimentoCopy.cartaoFornecedor + (totalPago - fornecedor.total);
        }
        saldoFinanceiroCopy.totalCusto =
          Number(saldoFinanceiroCopy.totalCusto) + Number(fornecedor.total);
      }

      resumoCopy.cash = recebimentoCopy.dinheiro - pagamentoCopy.cash;
      resumoCopy.creditoGerado = pagamentoCopy.pax || 0;
      fornecedoresTotaisCopy.total = saldoFinanceiroCopy.totalCusto;
      resumoCopy.aPagar = fornecedoresTotaisCopy.faltaPagar;
      resumoCopy.pagoFornecedor = fornecedoresTotaisCopy.valorPago;
      saldoFinanceiroCopy.total =
        saldoFinanceiroCopy.totalNet - saldoFinanceiroCopy.totalCusto;
      pagamentoCopy.total =
        (pagamentoCopy.cash || 0) +
        (pagamentoCopy.cartaoCredito || 0) +
        (pagamentoCopy.cartaoDebito || 0) +
        (pagamentoCopy.pix || 0) +
        (pagamentoCopy.pax || 0) +
        (pagamentoCopy.faturado || 0);
      recebimentoCopy.total =
        Number(recebimentoCopy.dinheiro || 0) +
        Number(recebimentoCopy.cartaoCredito || 0) +
        Number(recebimentoCopy.cartaoDebito || 0) +
        Number(recebimentoCopy.pix || 0) +
        Number(recebimentoCopy.cartaoFornecedor || 0) +
        Number(recebimentoCopy.taxas || 0);

      return {
        ...state,
        recebimento: recebimentoCopy,
        pagamento: pagamentoCopy,
        resumo: resumoCopy,
        saldoFinanceiro: saldoFinanceiroCopy,
        fornecedoresTotais: fornecedoresTotaisCopy,
      };
    }

    case "RESERVA_PAGAMENTO_ANTECIPADO_CHANGE_PARCELA_ACERTO_FINANCEIRO":
      var copyPagamento = [...state.fornecedor.pagamentos];
      var newParcela = { ...copyPagamento[action.index] };
      newParcela.parcela = action.payload;
      copyPagamento[action.index] = newParcela;
      return {
        ...state,
        fornecedor: { ...state.fornecedor, pagamentos: copyPagamento },
      };
    case "RESERVA_REMOVER_LINHA_PAGAMENTOS_ANTECIPADOS_ACERTO_FINANCEIRO": {
      const novosPagamentos = (state.fornecedor?.pagamentos || []).filter(
        (_, index) => index !== action.index
      );
      let fornecedorAtualizado = recalcularTotaisPagamentoFornecedor({
        ...state.fornecedor,
        pagamentos: novosPagamentos.map((pagamento) =>
          normalizarPagamentoFornecedor(pagamento)
        ),
      });

      let fornecedoresAtualizados = state.fornecedores;
      if (
        action.fornecedorIndex != null &&
        fornecedoresAtualizados[action.fornecedorIndex]
      ) {
        fornecedoresAtualizados = [...state.fornecedores];
        fornecedoresAtualizados[action.fornecedorIndex] = {
          ...fornecedoresAtualizados[action.fornecedorIndex],
          ...fornecedorAtualizado,
        };
        fornecedoresAtualizados = aplicarTaxaMaquininhaEmFornecedores(
          fornecedoresAtualizados,
          state.financeiros
        );
      }

      return {
        ...state,
        fornecedor: fornecedorAtualizado,
        fornecedores: fornecedoresAtualizados,
      };
    }
    case "RESERVA_PAGAMENTO_ANTECIPADO_CHANGE_BANCO":
      var copyPagamento = [...state.fornecedor.pagamentos];
      var newBanco = { ...copyPagamento[action.index] };
      newBanco.banco = action.payload;
      copyPagamento[action.index] = newBanco;
      return {
        ...state,
        fornecedor: { ...state.fornecedor, pagamentos: copyPagamento },
      };
    case "ACERTO_FETCH_LISTA":
      return {
        ...state,
        acertos: action.payload.data || action.payload,
        acertosPagination: {
          current_page: action.payload.current_page || 1,
          last_page: action.payload.last_page || 1,
          total: action.payload.total || 0,
        },
        acertosContadores: action.contadores || { PENDENTE: 0, CONFIRMADO: 0 }
      };
    case "ACERTO_FETCH": {
      const payload = action.payload || {};
      const rawList =
        payload.adiantamentos_reembolsos ||
        payload.adiantamentosReembolsos ||
        [];
      const adiantamentosReembolsos = Array.isArray(rawList)
        ? rawList.map(normalizeAdiantamentoItemFromApi)
        : [];
      return {
        ...state,
        acerto: action.payload,
        adiantamentosReembolsos,
      };
    }
    case "ACERTO_ADIANTAMENTOS_SET":
      return { ...state, adiantamentosReembolsos: action.payload || [] };
    case "ACERTO_ADIANTAMENTO_ADD": {
      const list = [...(state.adiantamentosReembolsos || [])];
      list.push(action.payload);
      return { ...state, adiantamentosReembolsos: list };
    }
    case "ACERTO_ADIANTAMENTO_REMOVE": {
      const list = [...(state.adiantamentosReembolsos || [])];
      list.splice(action.index, 1);
      return { ...state, adiantamentosReembolsos: list };
    }
    case "ACERTO_ADIANTAMENTO_UPDATE": {
      const list = [...(state.adiantamentosReembolsos || [])];
      if (list[action.index] !== undefined) {
        let merged = { ...list[action.index], ...action.payload };
        if (action.payload?.valor !== undefined) {
          merged.pagamentos = prepararPagamentosAdiantamento(merged).map((p) =>
            normalizarPagamentoFornecedor(p)
          );
        }
        const totais = calcularTotaisPagamentoAdiantamento(
          merged.valor,
          merged.pagamentos || [],
          itemEhDevolucaoGuia(merged)
        );
        merged.totalPago = totais.totalPago;
        merged.faltaPagar = totais.faltaPagar;
        list[action.index] = merged;
      }
      return { ...state, adiantamentosReembolsos: list };
    }
    case "ACERTO_ADIANTAMENTO_MODAL_SET": {
      const item = action.payload || {};
      const pagamentos = prepararPagamentosAdiantamento(item).map((p) =>
        normalizarPagamentoFornecedor(p)
      );
      const totais = calcularTotaisPagamentoAdiantamento(item.valor, pagamentos, itemEhDevolucaoGuia(item));
      return {
        ...state,
        adiantamentoModal: {
          index: action.index,
          valor: item.valor ?? "0",
          adiantamento_reembolso_tipo_id: item.adiantamento_reembolso_tipo_id ?? null,
          tipo: item.tipo ?? "",
          pagamentos,
          totalPago: totais.totalPago,
          faltaPagar: totais.faltaPagar,
        },
      };
    }
    case "ACERTO_ADIANTAMENTO_MODAL_PAGAMENTO_CHANGE": {
      const modal = { ...state.adiantamentoModal };
      const pagamentosCopy = [...(modal.pagamentos || [])];
      const rowCopy = { ...pagamentosCopy[action.index] };
      if (action.campoChange === "valor") {
        const raw = action.payload != null ? String(action.payload) : "";
        rowCopy.valor = raw.replace(",", ".");
      } else if (action.campoChange === "formaPagamento") {
        rowCopy.formaPagamento = action.payload;
        if (action.payload && action.payload.pag_credito === "SIM") {
          rowCopy.parcela = rowCopy.parcela || { label: "1X", value: 1 };
        }
        if (action.payload && Number(action.payload.value) !== 4) {
          rowCopy.banco = { label: "Selecione um banco!", value: 0 };
        }
      } else {
        rowCopy[action.campoChange] = action.payload;
      }
      pagamentosCopy[action.index] = rowCopy;
      modal.pagamentos = pagamentosCopy;
      const totais = calcularTotaisPagamentoAdiantamento(modal.valor, pagamentosCopy, itemEhDevolucaoGuia(modal));
      modal.totalPago = totais.totalPago;
      modal.faltaPagar = totais.faltaPagar;
      return { ...state, adiantamentoModal: modal };
    }
    case "ACERTO_ADIANTAMENTO_MODAL_PAGAMENTOS_SET": {
      const modal = { ...state.adiantamentoModal, pagamentos: action.payload || [] };
      const totais = calcularTotaisPagamentoAdiantamento(modal.valor, modal.pagamentos, itemEhDevolucaoGuia(modal));
      modal.totalPago = totais.totalPago;
      modal.faltaPagar = totais.faltaPagar;
      return { ...state, adiantamentoModal: modal };
    }
    case "ACERTO_ADIANTAMENTO_MODAL_PAGAMENTO_REMOVE": {
      const modal = { ...state.adiantamentoModal };
      modal.pagamentos = (modal.pagamentos || []).filter((_, idx) => idx !== action.index);
      const totais = calcularTotaisPagamentoAdiantamento(modal.valor, modal.pagamentos, itemEhDevolucaoGuia(modal));
      modal.totalPago = totais.totalPago;
      modal.faltaPagar = totais.faltaPagar;
      return { ...state, adiantamentoModal: modal };
    }
    case "ACERTO_ADIANTAMENTO_MODAL_PARCELA_CHANGE": {
      const modal = { ...state.adiantamentoModal };
      const pagamentosCopy = [...(modal.pagamentos || [])];
      const rowCopy = { ...pagamentosCopy[action.index] };
      rowCopy.parcela = action.payload;
      pagamentosCopy[action.index] = rowCopy;
      modal.pagamentos = pagamentosCopy;
      return { ...state, adiantamentoModal: modal };
    }
    case "ACERTO_ADIANTAMENTO_MODAL_BANCO_CHANGE": {
      const modal = { ...state.adiantamentoModal };
      const pagamentosCopy = [...(modal.pagamentos || [])];
      const rowCopy = { ...pagamentosCopy[action.index] };
      rowCopy.banco = action.payload;
      pagamentosCopy[action.index] = rowCopy;
      modal.pagamentos = pagamentosCopy;
      return { ...state, adiantamentoModal: modal };
    }
    case "ACERTO_ADIANTAMENTO_PAGAMENTOS_SALVAR": {
      const list = [...(state.adiantamentosReembolsos || [])];
      const modal = state.adiantamentoModal || {};
      if (list[action.index] !== undefined) {
        const pagamentos = Array.isArray(modal.pagamentos)
          ? modal.pagamentos.map((p) => normalizarPagamentoFornecedor(p))
          : [];
        const totais = calcularTotaisPagamentoAdiantamento(list[action.index].valor, pagamentos, itemEhDevolucaoGuia(list[action.index]));
        list[action.index] = {
          ...list[action.index],
          pagamentos,
          totalPago: totais.totalPago,
          faltaPagar: totais.faltaPagar,
        };
      }
      return {
        ...state,
        adiantamentosReembolsos: list,
        adiantamentoModal: {
          index: null,
          valor: "0",
          pagamentos: [],
          totalPago: 0,
          faltaPagar: 0,
        },
      };
    }
    case "FINANCEIRO_MODULO_CHANGE":
      return { ...state, modulo: action.payload };
    case "FORNECEDOR_VALOR_PENDENTE_PAGAMENTO": {
      var reservaCopy = { ...action.payload };
      var pago = 0;
      var total = reservaCopy.total;

      for (const pagamento of reservaCopy.pagamentos) {
        pago = pago + valorPagamentoComTaxa(pagamento);
      }

      if (total < 0) {
        total = total * -1;
      }

      reservaCopy.totalPagoForncedor = total - pago;

      if (reservaCopy.totalPagoForncedor > 0) {
        var novoPagamento = {
          valor: reservaCopy.totalPagoForncedor.toString(),
          fontePagamento: {
            label: "Selecione a fonte de pagamento!",
            value: 0,
          },
          formaPagamento: {
            label: "Selecione a forma de pagamento!",
            value: "",
          },
          banco: { label: "Selecione o banco!", value: 0 },
          parcela: { label: "1X", value: 1 },
          local: "",
        };

        // if(actionCopy?.novoCampo == true){
        reservaCopy.pagamentos = [...reservaCopy.pagamentos, novoPagamento]; // Adiciona o novo pagamento
        // }
      }

      return { ...state, fornecedor: reservaCopy };
    }

    case "ACERTO_CATEGORIAS_DIARIA_PASSEIO_FETCH":
      return { ...state, categoriasDiariaPasseio: action.payload };
    case "ACERTO_EDICAO_LIMPAR":
      return {
        ...state,
        acerto: {},
        financeiros: [],
        fornecedores: [],
        adiantamentosReembolsos: [],
        fornecedor: { parceiro: {}, pagamentos: [] },
        totaisRecebimentos: {
          total: 0,
          totalRecebidos: 0,
          valorCHD: 0,
          valorADT: 0,
          valorINF: 0,
          valorJVM: 0,
          valorIDS: 0,
        },
        recebimento: {
          dinheiro: 0,
          cartaoCredito: 0,
          cartaoDebito: 0,
          pix: 0,
          cartaoFornecedor: 0,
          taxas: 0,
          total: 0,
        },
        pagamento: {
          cash: 0,
          pax: 0,
          cartaoCredito: 0,
          cartaoDebito: 0,
          pix: 0,
          faturado: 0,
          desconto: 0,
          total: 0,
        },
        resumo: {
          cash: 0,
          comissao: 0,
          faturado: 0,
          aPagar: 0,
          pagoFornecedor: 0,
        },
        saldoFinanceiro: { totalNet: 0, totalCusto: 0, total: 0 },
        lucroBruto: {
          totalVendas: 0,
          totalCusto: 0,
          totalEncargos: 0,
          lucroBruto: 0,
        },
      };
    case "ACERTO_CLEAR_LISTA":
      return { ...state, financeiros: [], adiantamentosReembolsos: [] };

    case "ACERTO_SET_FILTRO": {
      const dadosFiltroCopy = { ...state.dadosFiltro };
      dadosFiltroCopy[action.campo] = action.valor;
      return { ...state, dadosFiltro: dadosFiltroCopy };
    }
    case "ACERTO_SET_FILTRO_CLEAR": {
      const dadosFiltroCopy = {
        filtroDataDe: "",
        filtroDataAte: "",
        filtroGuia: "",
        filtroUsuario: "",
        filtroTour: "",
        filtroPasseio: "",
        filtroDataConferencia: "",
      };
      return { ...state, dadosFiltro: dadosFiltroCopy };
    }
    case "ACERTO_MUDAR_TAB": {
      let activeTabCopy = { ...state.activeTab };

      activeTabCopy = action.payload;

      return { ...state, activeTab: activeTabCopy };
    }
    case "REPASSOS_PENDENTES_SET":
      return { ...state, repassesPendentes: action.payload || [] };
    case "REPASSOS_APROVADOS_SET":
      return { ...state, repassesAprovados: action.payload || [] };
    case "DEVOLUCOES_CONTAGENS_SET":
      return {
        ...state,
        devolucoesContagens: {
          ...state.devolucoesContagens,
          ...(action.payload || {}),
        },
      };
    case "ACERTO_CALCULAR_VALORES": {
      var copyFornecedores = Array.isArray(state.fornecedores)
        ? [...state.fornecedores]
        : [];
      var copyTotais = { ...state.totais };
      var modulo = action.payload;

      copyTotais.total = 0;

      for (let [index, fornecedor] of copyFornecedores.entries()) {
        if (fornecedor.cobranca == "PESSOA") {
          // Para cobrança por pessoa, NÃO incluir valorServico
          const valorADT = typeof fornecedor.valorADT === 'string' ? fornecedor.valorADT.replace(",", ".") : (fornecedor.valorADT || 0);
          const valorCHD = typeof fornecedor.valorCHD === 'string' ? fornecedor.valorCHD.replace(",", ".") : (fornecedor.valorCHD || 0);
          const valorINF = typeof fornecedor.valorINF === 'string' ? fornecedor.valorINF.replace(",", ".") : (fornecedor.valorINF || 0);
          const valorJVM = typeof fornecedor.valorJVM === 'string' ? fornecedor.valorJVM.replace(",", ".") : (fornecedor.valorJVM || 0);
          const valorIDSO = typeof fornecedor.valorIDSO === 'string' ? fornecedor.valorIDSO.replace(",", ".") : (fornecedor.valorIDSO || 0);

          fornecedor.total =
            Number(valorADT) *
            Number(fornecedor.qtd_adt || 0) +
            Number(valorCHD) *
            Number(fornecedor.qtd_chd || 0) +
            Number(valorINF) *
            Number(fornecedor.qtd_inf || 0) +
            Number(valorJVM) *
            Number(fornecedor.qtd_jovem || 0) +
            Number(valorIDSO) *
            Number(fornecedor.qtd_idoso || 0);
        } else if (fornecedor.cobranca == "SERVICO") {
          fornecedor.total =
            Number(fornecedor.valorServico || 0) *
            Number(fornecedor.qtd_servico || 0);
        }

        copyFornecedores[index] = fornecedor;

        if (modulo == "cadastro") {
          // console.log(modulo, 'modulo')
          copyFornecedores.map((item) => {
            copyTotais.total += item.total;
          });

          // Calcular total pago excluindo pagamentos "FATURADO" (ID 1), incluindo taxa de cartão
          const fornecedorIdLinhaModulo = Number(fornecedor?.fornecedor?.value || 0);
          var totalPagoCalculado = 0;
          if (fornecedor.pagamentos && fornecedor.pagamentos.length > 0) {
            for (const pagamento of fornecedor.pagamentos) {
              if (pagamento?.formaPagamento?.value !== 1) {
                totalPagoCalculado = totalPagoCalculado + valorPagamentoComTaxa(
                  pagamento,
                  fornecedorIdLinhaModulo
                );
              }
            }
          }
          fornecedor.faltaPagar = fornecedor.total - totalPagoCalculado;
        }
      }

      var pagamentosCopy = { ...state.pagamentos };
      var totaisFinanceirosCopy = { ...state.totaisFinanceiros };

      var fornecedorDinheiro = copyFornecedores?.filter(
        (item) => item.formaPagamento && item.formaPagamento.pag_cash === "SIM"
      );
      var totalDinheiro = fornecedorDinheiro.reduce(
        (acumulador, objeto) => acumulador + Number(objeto.valorPago || 0),
        0
      );
      pagamentosCopy.totalDinheiro = totalDinheiro;

      var fornecedorDinheiro = copyFornecedores?.filter(
        (item) => item.formaPagamento && item.formaPagamento.pag_credito === "SIM"
      );
      var totalCredito = fornecedorDinheiro.reduce(
        (acumulador, objeto) => acumulador + Number(objeto.valorPago || 0),
        0
      );
      pagamentosCopy.totalCredito = totalCredito;

      var fornecedorDinheiro = copyFornecedores?.filter(
        (item) => item.formaPagamento && item.formaPagamento.pag_debito === "SIM"
      );
      var totalDebito = fornecedorDinheiro.reduce(
        (acumulador, objeto) => acumulador + Number(objeto.valorPago || 0),
        0
      );
      pagamentosCopy.totalDebitoConta = totalDebito;

      var fornecedorDinheiro = copyFornecedores?.filter(
        (item) => item.formaPagamento && item.formaPagamento.value == 10
      );
      var totalCartaoPax = fornecedorDinheiro.reduce(
        (acumulador, objeto) => acumulador + Number(objeto.valorPago || 0),
        0
      );

      pagamentosCopy.totalCartaoPax = totalCartaoPax;
      totaisFinanceirosCopy.pagoFornecedor = totalCartaoPax;

      return {
        ...state,
        fornecedores: copyFornecedores,
        totais: copyTotais,
        pagamentos: pagamentosCopy,
        totaisFinanceiros: totaisFinanceirosCopy,
      };
    }
    default:
      return state;
  }
};
